import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UclRouteObject } from "../../utils/routes";

interface ContainerState {
  key: string;
  routeObject: UclRouteObject | null;
}

interface RouteContainerStateContextType {
  activeContainers: ContainerState[];
  setContainerState: (key: string | null) => void;
  getContainerState: (key: string) => ContainerState | null;
  allRouteContainerObjects: (UclRouteObject | null)[];
  navigateToContainer: (basePath: string, key: string) => void;
}

interface RouteContainerStateProviderProps {
  children: React.ReactNode;
  routes: UclRouteObject[];
}

const RouteContainerStateContext = createContext<
  RouteContainerStateContextType | undefined
>(undefined);

export const RouteContainerStateProvider: React.FC<
  RouteContainerStateProviderProps
> = ({ children, routes }) => {
  const [activeContainers, setActiveContainers] = useState<ContainerState[]>(
    []
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to find all routes with containerKey
  const findRoutesWithContainerKey = (
    routes: UclRouteObject[]
  ): UclRouteObject[] => {
    const routesWithKey: UclRouteObject[] = [];

    const traverse = (routeList: UclRouteObject[]) => {
      for (const route of routeList) {
        if (route.containerKey) {
          routesWithKey.push(route);
        }
        if (route.children) {
          traverse(route.children);
        }
      }
    };

    traverse(routes);
    return routesWithKey;
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const routesWithContainerKey = findRoutesWithContainerKey(routes);

    // Find routes whose containerKey is in the current path
    const matchingRoutes = routesWithContainerKey.filter((route) =>
      currentPath.includes(route.containerKey || "")
    );

    if (matchingRoutes.length > 0) {
      setActiveContainers(
        matchingRoutes.map((route) => ({
          key: route.containerKey || "",
          routeObject: route,
        }))
      );
    }
  }, [location.pathname, routes]);

  const setContainerState = (key: string | null) => {
    if (!key) return;

    const routesWithContainerKey = findRoutesWithContainerKey(routes);
    const matchingRoute = routesWithContainerKey.find(
      (route) => route.containerKey === key
    );

    if (matchingRoute) {
      navigate(matchingRoute.path || "");
    }

    setActiveContainers((prev) => {
      const filtered = prev.filter((c) => c.key !== key);
      return [...filtered, { key, routeObject: matchingRoute || null }];
    });
  };

  const getContainerState = (key: string) => {
    return activeContainers.find((c) => c.key === key) || null;
  };

  const navigateToContainer = (basePath: string, key: string) => {
    if (basePath) {
      const routeObject = (findRoutesWithContainerKey(routes) || []).find(
        (route) => route?.containerKey === key
      );

      if (routeObject && routeObject.path) {
        // Navigate relative to current location
        // navigate(routeObject.path, { relative: "route" });
        navigate(basePath + "/" + routeObject.path);
      }
    }
  };
  return (
    <RouteContainerStateContext.Provider
      value={{
        activeContainers,
        setContainerState,
        getContainerState,
        allRouteContainerObjects: findRoutesWithContainerKey(routes) || [],
        navigateToContainer,
      }}
    >
      {children}
    </RouteContainerStateContext.Provider>
  );
};

export const useRouteContainerState = () => {
  const context = useContext(RouteContainerStateContext);
  if (context === undefined) {
    throw new Error(
      "useRouteContainerState must be used within a RouteContainerStateProvider"
    );
  }
  return context;
};

// Convenience hooks for specific containers
export const useRouteContainer = (
  basePath?: string,
  containerKeys?: string[]
) => {
  const { getContainerState, allRouteContainerObjects, navigateToContainer } =
    useRouteContainerState();

  if (!basePath) {
    console.warn(
      "No baseUrl provided. Calling navigateToContainer will do nothing."
    );
    return {
      activeKey: null,
      routeObject: null,
      allRouteContainerObjects: [],
      navigateToContainer: console.warn,
    };
  }

  // Return routeObject
  const activeKey =
    containerKeys?.find((key) => getContainerState(key)) || null;
  return {
    activeKey,
    routeObject: getContainerState(activeKey || "")?.routeObject,
    allRouteContainerObjects: allRouteContainerObjects,
    navigateToContainer: (key: string) => {
      navigateToContainer(basePath, key);
    },
  };
};
